import React from 'react';
import { Box } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format as formatDate } from 'date-fns';
import { getReservationSlotsApi } from '../../../utils/apis';
import NoDataFallback from '../../common/NoDataFallback';
import AvaiableTablesSkeleton from './AvaiableTablesSkeleton';
interface TableRef {
  refresh: () => Promise<void>;
}

const formatDateTime = (dateTimeStr: string) => {
  const date = new Date(dateTimeStr);

  // Format date as Apr 14, 2025
  const formattedDate = formatDate(date, 'MMM dd, yyyy');

  // Get hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';

  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // Convert 0 to 12
  const formattedHours = hours.toString().padStart(2, '0');

  // Calculate end time
  let endHours = hours;
  let endMinutes = parseInt(minutes) + 15;
  let endAmPm = ampm;

  if (endMinutes >= 60) {
    endMinutes = 0;
    endHours += 1;
    if (endHours > 12) {
      endHours = 1;
      endAmPm = ampm === 'AM' ? 'PM' : 'AM';
    }
  }

  const timeRange = `${formattedHours}:${minutes} ${ampm} - ${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')} ${endAmPm}`;

  return {
    date: formattedDate,
    time: timeRange,
  };
};

interface AvaiableSeatsTableProps {
  onRefreshStart?: () => void;
  onRefreshComplete?: () => void;
  selectedStore: any;
  isStoreLoading: boolean;
}

const AvaiableSeatsTable = React.forwardRef<TableRef, AvaiableSeatsTableProps>(
  (
    { onRefreshStart, onRefreshComplete, selectedStore, isStoreLoading },
    ref
  ) => {
    const queryClient = useQueryClient();
    const today = formatDate(new Date(), 'yyyy-MM-dd');

    const {
      data: reservationSlots,
      isLoading: isReservationSlotsLoading,
      isError: isReservationSlotsError,
      error: reservationSlotsError,
    } = useQuery({
      queryKey: ['reservationSlots', selectedStore],
      queryFn: () =>
        getReservationSlotsApi(
          selectedStore?.reservation?.restaurantId || 'dsawwa',
          today
        ),
      gcTime: 1000 * 60 * 30,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 0,
      enabled: Boolean(selectedStore),
    });

    const refreshData = async () => {
      onRefreshStart?.();
      await queryClient.invalidateQueries({ queryKey: ['reservationSlots'] });
      onRefreshComplete?.();
    };

    React.useImperativeHandle(ref, () => ({
      refresh: refreshData,
    }));

    const rows = React.useMemo(() => {
      if (!reservationSlots?.data?.data) return [];

      return reservationSlots.data.data.timeslots.map(
        (slot: any, index: number) => {
          const { date, time } = formatDateTime(slot.slot_start_time_friendly);
          return {
            id: index,
            date,
            time,
            available_covers_count: slot.available_covers_count || '-',
          };
        }
      );
    }, [reservationSlots]);

    const columns: GridColDef[] = [
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        headerClassName: 'custom-header',
      },
      {
        field: 'time',
        headerName: 'Time',
        flex: 1,
        headerClassName: 'custom-header',
      },
      {
        field: 'available_covers_count',
        headerName: 'Available Seats',
        flex: 1,
        headerClassName: 'custom-header',
      },
    ];

    if (isReservationSlotsLoading || isStoreLoading) {
      return <AvaiableTablesSkeleton />;
    }

    if (isReservationSlotsError) {
      return (
        <NoDataFallback
          httpStatusCode={(reservationSlotsError as any)?.status}
        />
      );
    }

    if (!reservationSlots?.data?.data) {
      return (
        <NoDataFallback
          message="No Reservation Slots available"
          description="There are no reservation slots available for this store."
        />
      );
    }

    return (
      <Box sx={{ height: '100%', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 100 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 75, 100]}
          pagination
          disableRowSelectionOnClick
          sx={{
            borderRadius: '8px',
            color: 'rgba(107, 111, 128, 1)',
            '& .MuiDataGrid-root': {
              borderRadius: '8px',
              overflow: 'hidden',
            },
            '& .MuiDataGrid-main': {
              borderRadius: '8px',
              overflow: 'hidden',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
            },
            '& .MuiDataGrid-columnHeader': {
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
              bgcolor: '#F8F9FC',
            },
            '& .custom-header': {
              bgcolor: '#F8F9FC',
              color: 'custom.textBlue',
              fontSize: '14px',
              fontWeight: 600,
              padding: '12px 16px',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'rgba(43, 45, 63, 1)',
              fontWeight: 600,
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'transparent !important',
              border: 'none !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: '#E7F0FF !important',
            },
            '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
          }}
        />
      </Box>
    );
  }
);

export default AvaiableSeatsTable;
